// components/Pagination.tsx
import { ArrowLeft, ArrowRight } from 'lucide-react';
import React from 'react';
import Dropdown from './Dropdown';

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
  className?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  className = '',
}) => {
  const totalPages = Math.ceil(totalItems / pageSize);

  // Generate visible page numbers (like: 1 2 3 ... 8 9 10)
  const getVisiblePages = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Add first page and ellipsis if needed
    if (startPage > 1) {
      pages.unshift(1);
      if (startPage > 2) pages.splice(1, 0, -1); // -1 represents ellipsis
    }

    // Add last page and ellipsis if needed
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pages.push(-1);
      pages.push(totalPages);
    }

    return pages;
  };

  const visiblePages = getVisiblePages();

  if (totalItems === 0) return null;

  return (
    <div
      className={`flex flex-col gap-4 py-1 sm:py-3 text-text md:flex-row md:items-center md:justify-between ${className}`}
    >
      {/* Count Dropdown */}
      <div className="w-full md:w-auto">
        <Dropdown
          filterOptions={pageSizeOptions.map((size) => size.toString())}
          onFilterChange={(selectedValue) => {
            const size = Number(selectedValue);
            onPageSizeChange(size);
          }}
          title="Count"
          width="w-full md:w-40"
        />
      </div>

      {/* Page Numbers */}
      <div className="flex flex-wrap items-center justify-center gap-1">
        {visiblePages.map((page, index) => {
          if (page === -1) {
            return (
              <span key={`ellipsis-${index}`} className="px-2 py-1 text-sm text-muted-foreground">
                ...
              </span>
            );
          }

          return (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`min-w-[36px] rounded-md px-3 py-2 text-sm font-medium transition ${
                currentPage === page ? 'bg-gold text-gold-foreground' : 'text-muted-foreground hover:bg-raised'
              }`}
            >
              {page}
            </button>
          );
        })}
      </div>

      {/* Prev / Next Buttons */}
      <div className="grid w-full grid-cols-2 gap-3 md:w-auto">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="flex items-center justify-center gap-2 rounded-lg border border-border bg-white px-4 py-2 transition hover:border-gold hover:text-gold disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border disabled:hover:text-inherit"
        >
          <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
          <span className="text-sm sm:text-base">Previous</span>
        </button>

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="flex items-center justify-center gap-2 rounded-lg border border-border bg-white px-4 py-2 transition hover:border-gold hover:text-gold disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border disabled:hover:text-inherit"
        >
          <span className="text-sm sm:text-base">Next</span>
          <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
